import { useQuery } from "@tanstack/react-query";
import {
  type BacklogItem,
  type HomeWidgetSnapshot,
  type PendingDenialSummary,
  type ProductCoverage,
  distributionService,
} from "../services/distributionService";

const COVERAGE_STALE_MS = 60 * 1000; // 1 minuto
const BACKLOG_STALE_MS = 5 * 60 * 1000; // 5 minutos
const WIDGET_STALE_MS = 2 * 60 * 1000;

export function useProductCoverage(
  unifiedProductId: string | null,
  enabled = true,
): {
  data: ProductCoverage | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const query = useQuery({
    queryKey: ["distribution", "coverage", unifiedProductId],
    queryFn: () => {
      if (!unifiedProductId) {
        throw new Error("unifiedProductId requerido");
      }
      return distributionService.getCoverage(unifiedProductId);
    },
    enabled: enabled && Boolean(unifiedProductId),
    staleTime: COVERAGE_STALE_MS,
    retry: 1,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => {
      void query.refetch();
    },
  };
}

export function useDistributionBacklog(enabled = true): {
  data: BacklogItem[] | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const query = useQuery({
    queryKey: ["distribution", "backlog"],
    queryFn: () => distributionService.getDistributionBacklog(),
    enabled,
    staleTime: BACKLOG_STALE_MS,
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export function usePendingDenials(enabled = true): {
  data: PendingDenialSummary[] | undefined;
  isLoading: boolean;
} {
  const query = useQuery({
    queryKey: ["distribution", "pending-denials"],
    queryFn: () => distributionService.getPendingDenials(),
    enabled,
    staleTime: BACKLOG_STALE_MS,
  });
  return { data: query.data, isLoading: query.isLoading };
}

export function useHomeWidgetCompras(enabled = true): {
  data: HomeWidgetSnapshot | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const query = useQuery({
    queryKey: ["distribution", "home-widget"],
    queryFn: () => distributionService.getHomeWidget(),
    enabled,
    staleTime: WIDGET_STALE_MS,
    refetchInterval: WIDGET_STALE_MS,
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => {
      void query.refetch();
    },
  };
}
